/**
 * The staff portal shell: sidebar, header, and the routed page.
 *
 * The navigation is the volunteer lifecycle in order — Registrations, Volunteers,
 * Programs, Activities, Attendance. Section 9.37 measures progress in journeys,
 * not screens, and the sidebar is where staff first read what the journey is.
 *
 * The public registration form does NOT use this layout. It has no sidebar, no
 * staff navigation and no link back into the portal (Section 1.7).
 */

import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { BrandLogo, PasswordNotice } from '../components/common';
import { useAuth } from '../hooks/useAuth';
import { useHealth } from '../hooks/useHealth';
import styles from './AppLayout.module.css';

/**
 * Plan D12 removed `Registrations` from here.
 *
 * New submissions appear at the top of `Volunteers`, which is where staff act on
 * them; a second entry pointing at a second screen for the same job is how a
 * small team ends up unsure which one is authoritative. The archive of every
 * submission ever received still exists at `/registrations` and is linked from
 * that panel — out of the sidebar, not out of the application.
 */
/**
 * One shape for every entry, so the three lists can be concatenated without the
 * union widening away `end` - which is what marks the index link and stops it
 * highlighting on every route beneath it.
 */
interface NavItem {
  to: string;
  label: string;
  /** Match this path exactly. Set on "/" so it is not active everywhere. */
  end?: boolean;
}

const STAFF_NAVIGATION: NavItem[] = [
  { to: '/', label: 'Dashboard', end: true },
  { to: '/volunteers', label: 'Volunteers' },
  { to: '/programs', label: 'Programs' },
  { to: '/activities', label: 'Activities' },
  { to: '/attendance', label: 'Attendance' },
  /*
   * Staff, not admin, since refinement 2: a field coordinator is asked at the
   * door whether somebody has had a certificate, and now has a screen that
   * answers it. The page shows a TEAM account the reading tab and no issuing
   * tabs, and the API refuses every POST to a non-admin regardless.
   */
  { to: '/admin/certificates', label: 'Certificates' },
];

/**
 * Appended for ADMIN accounts only.
 *
 * Kept out of `STAFF_NAVIGATION` rather than filtered out of it, because of
 * where these two lead: one erases the program data, and the other downloads
 * every volunteer's contact details into a file that leaves the portal for
 * good. A list everybody renders and one role hides is the shape where a
 * refactor accidentally shows it; a list that is only ever concatenated for
 * admins is not.
 *
 * Hiding the links is presentation, not security - the API refuses a TEAM
 * account outright, and each page says "admins only" if one navigates to it.
 */
const ADMIN_NAVIGATION: NavItem[] = [
  // Certificates moved to STAFF_NAVIGATION — see the note there.
  // Export before Data tools: it is the one an admin comes back to, and the
  // other is the one they should have to mean.
  { to: '/admin/export', label: 'Export' },
  { to: '/admin/accounts', label: 'Accounts' },
  { to: '/admin/data', label: 'Data tools' },
];

/**
 * The volunteer's own three.
 *
 * One shell for every role, rather than a separate layout per audience. A
 * volunteer was briefly given a bare header of their own, on the reasoning that
 * a sidebar with four of five links removed reads as a system somebody has been
 * locked out of. Two shells turned out to be the worse problem: the same
 * product behaving differently depending on who signs in is harder to explain,
 * harder to style once, and harder to keep consistent as either side grows.
 *
 * So the shell is the same and the navigation is what differs — which is what
 * every other application does, and what people expect.
 */
const VOLUNTEER_NAVIGATION: NavItem[] = [
  { to: '/', label: 'My volunteering', end: true },
  { to: '/opportunities', label: 'Find activities' },
  { to: '/my-hours', label: 'My hours' },
];

/**
 * Who is signed in, and the way out.
 *
 * In the sidebar rather than a header menu behind an avatar: on a shared office
 * machine "am I still signed in as the coordinator who used this before me?" is
 * a question worth answering without a click, because the answer decides whose
 * name lands in the audit trail.
 */
function SignedInAs() {
  const { user, signOut } = useAuth();
  const [pending, setPending] = useState(false);

  if (!user) return null;

  const signOutNow = async () => {
    setPending(true);
    try {
      await signOut();
      // No navigate() here: clearing the user re-renders the guard, which
      // sends us to the sign-in page. Doing both would race the router — and
      // there is nothing left to correct, because the guard no longer carries
      // the page we were on.
    } catch {
      // signOut clears the session whatever happens; there is nothing left for
      // this component to do, and an unhandled rejection would only surface in
      // the console.
    } finally {
      setPending(false);
    }
  };

  return (
    <div className={styles.account}>
      <p className={styles.accountName}>{user.display_name}</p>
      <p className={styles.accountRole}>{roleLabel(user.role)}</p>
      {/*
        Here rather than in the navigation above: this is about the account,
        not about the work, and it belongs with the name it concerns and the
        way out. Every role gets it — an admin’s password does not expire, but
        it is still the only place they can change it.
      */}
      <NavLink to="/profile" className={styles.accountLink}>
        Your account
      </NavLink>
      <button
        type="button"
        className={styles.signOut}
        onClick={() => void signOutNow()}
        disabled={pending}
      >
        {pending ? 'Signing out…' : 'Sign out'}
      </button>
    </div>
  );
}

/** The role in the words staff use, not the enum. */
function roleLabel(role: 'ADMIN' | 'TEAM' | 'VOLUNTEER'): string {
  if (role === 'ADMIN') return 'Administrator';
  if (role === 'TEAM') return 'Parinaam team';
  return 'Volunteer';
}

/**
 * The three bars, and a cross once the menu is open.
 *
 * Inline rather than from an icon set, for the same reason the eye in
 * `Field.tsx` is: a dependency for two glyphs is not a trade worth making.
 * `aria-hidden`, because the button around it carries the name.
 */
function MenuIcon({ open }: { open: boolean }) {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
      {open ? (
        <path
          d="M5 5 19 19M19 5 5 19"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
      ) : (
        <path
          d="M3.5 6.5h17M3.5 12h17M3.5 17.5h17"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
      )}
    </svg>
  );
}

export function AppLayout() {
  const { user, isStaff, hasRole } = useAuth();
  const navigation = isStaff
    ? [...STAFF_NAVIGATION, ...(hasRole('ADMIN') ? ADMIN_NAVIGATION : [])]
    : VOLUNTEER_NAVIGATION;

  /*
   * The menu, on a phone only.
   *
   * Below 800px the sidebar cannot sit beside the page, and the version that
   * put it above instead was worse than no navigation at all: ten links and
   * the whole signed-in block — the name, the role, and a Sign out button —
   * came before every page, so the first thing on screen anywhere in the
   * portal was the way out of it.
   *
   * It is a drawer now. Closed, the page starts under the banner; open, it is
   * the same vertical list of links the sidebar has always been. The state is
   * only ever read by CSS, so the desktop layout does not consult it and the
   * markup is identical at both sizes.
   */
  const [menuOpen, setMenuOpen] = useState(false);
  const { pathname } = useLocation();

  // Following a link is the end of the visit to the menu. Without this the
  // drawer stays open over the page the person just asked for.
  useEffect(() => setMenuOpen(false), [pathname]);

  return (
    <div className={styles.shell}>
      {/*
        The banner spans the full width above everything: the logo on the left,
        the product title beside it. The brand used to live in the sidebar; a
        horizontal banner is where a staff member first reads what the
        application is, and the logo already carries the Parinaam name.
      */}
      <header className={styles.banner}>
        {/* Hidden above 800px, where the sidebar is simply there. */}
        <button
          type="button"
          className={styles.menuButton}
          aria-expanded={menuOpen}
          aria-controls="portal-navigation"
          onClick={() => setMenuOpen((open) => !open)}
        >
          <MenuIcon open={menuOpen} />
          Menu
        </button>

        <BrandLogo />
        <span className={styles.bannerTitle}>
          {isStaff ? 'Volunteer Management System' : 'My Volunteering'}
        </span>
      </header>

      <div className={styles.body}>
        <aside
          id="portal-navigation"
          className={styles.sidebar}
          // Absent rather than `false`: the stylesheet asks whether the
          // attribute is there, and `data-open="false"` is there.
          data-open={menuOpen || undefined}
        >
          <nav className={styles.nav} aria-label="Main">
            {navigation.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `${styles.navLink} ${isActive ? styles.navActive : ''}`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <SignedInAs />
          <ConnectionIndicator />
        </aside>

        <main className={styles.main}>
          <div className={styles.content}>
            {/*
              Above the page, inside the scrolling area rather than pinned: it
              is a thing to act on once, not a permanent chrome element, and a
              banner that cannot be scrolled past is a banner people resent.
              It renders nothing outside the fortnight before expiry.
            */}
            {user?.password && <PasswordNotice status={user.password} />}
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}

/**
 * A small, permanent readout of whether the API and database are up.
 *
 * Section 12.28: "the API is not running" is the most common local problem, and a
 * screen full of empty tables does not say so. This does.
 */
function ConnectionIndicator() {
  const { data, isError, isPending } = useHealth();

  // isPending, not isLoading: during the gap between a failed check and its
  // retry the latter is false, and the dot would claim the API is down when it
  // has not finished asking.
  const state = isPending ? 'checking' : isError || data?.status !== 'ok' ? 'down' : 'ok';

  const label =
    state === 'checking'
      ? 'Checking API…'
      : state === 'ok'
        ? 'API connected'
        : 'API unavailable — is the backend running?';

  return (
    <div className={styles.status} role="status">
      <span className={`${styles.dot} ${styles[state]}`} aria-hidden="true" />
      <span className={styles.statusLabel}>{label}</span>
    </div>
  );
}
